import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Card, Row, Col, Badge, Spinner, Tabs, Tab } from 'react-bootstrap';
import { FaShieldAlt, FaHeartbeat, FaCar, FaHome, FaPlane, FaUser, FaCalendarAlt, FaRupeeSign, FaCheckCircle } from 'react-icons/fa';

const InsuranceModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('life');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    mobile: '',
    email: '',
    age: '',
    gender: ''
  });
  const [selectedCoverage, setSelectedCoverage] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [quoteDetails, setQuoteDetails] = useState(null);

  // Insurance providers and plans
  const insuranceTypes = [
    { key: 'life', label: 'Life Insurance', icon: <FaUser className="text-primary" /> },
    { key: 'health', label: 'Health Insurance', icon: <FaHeartbeat className="text-success" /> },
    { key: 'motor', label: 'Motor Insurance', icon: <FaCar className="text-warning" /> },
    { key: 'home', label: 'Home Insurance', icon: <FaHome className="text-info" /> },
    { key: 'travel', label: 'Travel Insurance', icon: <FaPlane className="text-danger" /> },
    { key: 'crop', label: 'Crop Insurance', icon: <FaShieldAlt className="text-success" /> },
    { key: 'marine', label: 'Marine Insurance', icon: <FaShieldAlt className="text-info" /> },
    { key: 'fire', label: 'Fire Insurance', icon: <FaShieldAlt className="text-danger" /> }
  ];

  const providers = {
    life: [
      { id: 'lic', name: 'LIC of India', rating: 4.2, plans: ['Term Plan', 'Endowment', 'ULIP', 'Pension'], logo: 'lic.png' },
      { id: 'hdfc_life', name: 'HDFC Life', rating: 4.1, plans: ['Term Plan', 'ULIP', 'Savings Plan'], logo: 'hdfc-life.png' },
      { id: 'icici_life', name: 'ICICI Prudential', rating: 4.0, plans: ['Term Plan', 'Child Plan', 'ULIP'], logo: 'icici-life.png' },
      { id: 'sbi_life', name: 'SBI Life', rating: 3.9, plans: ['Term Plan', 'Endowment', 'Pension'], logo: 'sbi-life.png' },
      { id: 'max_life', name: 'Max Life Insurance', rating: 4.0, plans: ['Term Plan', 'ULIP', 'Savings'], logo: 'max-life.png' },
      { id: 'kotak_life', name: 'Kotak Life Insurance', rating: 3.9, plans: ['Term Plan', 'ULIP', 'Pension'], logo: 'kotak-life.png' },
      { id: 'bajaj_life', name: 'Bajaj Allianz Life', rating: 3.8, plans: ['Term Plan', 'ULIP', 'Savings'], logo: 'bajaj-life.png' },
      { id: 'tata_life', name: 'Tata AIA Life', rating: 3.8, plans: ['Term Plan', 'ULIP', 'Pension'], logo: 'tata-life.png' },
      { id: 'birla_life', name: 'Aditya Birla Sun Life', rating: 3.7, plans: ['Term Plan', 'ULIP', 'Savings'], logo: 'birla-life.png' },
      { id: 'reliance_life', name: 'Reliance Nippon Life', rating: 3.6, plans: ['Term Plan', 'ULIP', 'Pension'], logo: 'reliance-life.png' },
      { id: 'exide_life', name: 'Exide Life Insurance', rating: 3.5, plans: ['Term Plan', 'ULIP', 'Savings'], logo: 'exide-life.png' },
      { id: 'aviva_life', name: 'Aviva Life Insurance', rating: 3.4, plans: ['Term Plan', 'ULIP', 'Pension'], logo: 'aviva-life.png' }
    ],
    health: [
      { id: 'star_health', name: 'Star Health', rating: 4.3, plans: ['Individual', 'Family', 'Senior Citizen'], logo: 'star-health.png' },
      { id: 'hdfc_ergo', name: 'HDFC ERGO', rating: 4.1, plans: ['Individual', 'Family', 'Critical Illness'], logo: 'hdfc-ergo.png' },
      { id: 'icici_lombard', name: 'ICICI Lombard', rating: 4.0, plans: ['Family', 'Individual', 'Top-up'], logo: 'icici-lombard.png' },
      { id: 'bajaj_allianz', name: 'Bajaj Allianz', rating: 3.9, plans: ['Family', 'Senior Citizen', 'Individual'], logo: 'bajaj-allianz.png' },
      { id: 'new_india', name: 'New India Assurance', rating: 4.0, plans: ['Individual', 'Family', 'Group'], logo: 'new-india.png' },
      { id: 'oriental', name: 'Oriental Insurance', rating: 3.8, plans: ['Individual', 'Family', 'Senior'], logo: 'oriental.png' },
      { id: 'united_india', name: 'United India Insurance', rating: 3.7, plans: ['Individual', 'Family', 'Group'], logo: 'united-india.png' },
      { id: 'national_insurance', name: 'National Insurance', rating: 3.6, plans: ['Individual', 'Family', 'Senior'], logo: 'national-insurance.png' },
      { id: 'apollo_munich', name: 'Apollo Munich', rating: 4.2, plans: ['Individual', 'Family', 'Critical Illness'], logo: 'apollo-munich.png' },
      { id: 'cigna_ttk', name: 'Cigna TTK', rating: 3.9, plans: ['Individual', 'Family', 'Senior'], logo: 'cigna-ttk.png' },
      { id: 'manipal_cigna', name: 'Manipal Cigna', rating: 3.8, plans: ['Individual', 'Family', 'Senior'], logo: 'manipal-cigna.png' },
      { id: 'reliance_health', name: 'Reliance General', rating: 3.7, plans: ['Individual', 'Family', 'Group'], logo: 'reliance-general.png' }
    ],
    motor: [
      { id: 'bajaj_allianz_motor', name: 'Bajaj Allianz', rating: 4.2, plans: ['Comprehensive', 'Third Party', 'Zero Dep'], logo: 'bajaj-allianz.png' },
      { id: 'hdfc_ergo_motor', name: 'HDFC ERGO', rating: 4.1, plans: ['Comprehensive', 'Third Party'], logo: 'hdfc-ergo.png' },
      { id: 'icici_lombard_motor', name: 'ICICI Lombard', rating: 4.0, plans: ['Comprehensive', 'Pay as You Drive'], logo: 'icici-lombard.png' },
      { id: 'tata_aig', name: 'Tata AIG', rating: 3.8, plans: ['Comprehensive', 'Third Party'], logo: 'tata-aig.png' },
      { id: 'new_india_motor', name: 'New India Assurance', rating: 4.0, plans: ['Comprehensive', 'Third Party', 'Zero Dep'], logo: 'new-india.png' },
      { id: 'oriental_motor', name: 'Oriental Insurance', rating: 3.8, plans: ['Comprehensive', 'Third Party'], logo: 'oriental.png' },
      { id: 'united_india_motor', name: 'United India Insurance', rating: 3.7, plans: ['Comprehensive', 'Third Party'], logo: 'united-india.png' },
      { id: 'national_motor', name: 'National Insurance', rating: 3.6, plans: ['Comprehensive', 'Third Party'], logo: 'national-insurance.png' },
      { id: 'reliance_general', name: 'Reliance General', rating: 3.9, plans: ['Comprehensive', 'Third Party', 'Zero Dep'], logo: 'reliance-general.png' },
      { id: 'future_generali', name: 'Future Generali', rating: 3.8, plans: ['Comprehensive', 'Third Party'], logo: 'future-generali.png' },
      { id: 'royal_sundaram', name: 'Royal Sundaram', rating: 3.7, plans: ['Comprehensive', 'Third Party'], logo: 'royal-sundaram.png' },
      { id: 'liberty_general', name: 'Liberty General', rating: 3.6, plans: ['Comprehensive', 'Third Party'], logo: 'liberty-general.png' }
    ],
    home: [
      { id: 'hdfc_ergo_home', name: 'HDFC ERGO', rating: 4.0, plans: ['Structure', 'Contents', 'Comprehensive'], logo: 'hdfc-ergo.png' },
      { id: 'bajaj_allianz_home', name: 'Bajaj Allianz', rating: 3.9, plans: ['Bharat Griha Raksha', 'Comprehensive'], logo: 'bajaj-allianz.png' },
      { id: 'icici_lombard_home', name: 'ICICI Lombard', rating: 3.8, plans: ['Home Shield', 'Comprehensive'], logo: 'icici-lombard.png' },
      { id: 'tata_aig_home', name: 'Tata AIG', rating: 3.7, plans: ['Home Protect', 'Comprehensive'], logo: 'tata-aig.png' },
      { id: 'new_india_home', name: 'New India Assurance', rating: 3.9, plans: ['Home Shield', 'Comprehensive'], logo: 'new-india.png' },
      { id: 'oriental_home', name: 'Oriental Insurance', rating: 3.7, plans: ['Home Protect', 'Comprehensive'], logo: 'oriental.png' },
      { id: 'united_india_home', name: 'United India Insurance', rating: 3.6, plans: ['Home Shield', 'Comprehensive'], logo: 'united-india.png' },
      { id: 'national_home', name: 'National Insurance', rating: 3.5, plans: ['Home Protect', 'Comprehensive'], logo: 'national-insurance.png' },
      { id: 'reliance_home', name: 'Reliance General', rating: 3.8, plans: ['Home Shield', 'Comprehensive'], logo: 'reliance-general.png' },
      { id: 'future_home', name: 'Future Generali', rating: 3.7, plans: ['Home Protect', 'Comprehensive'], logo: 'future-generali.png' },
      { id: 'royal_sundaram_home', name: 'Royal Sundaram', rating: 3.6, plans: ['Home Shield', 'Comprehensive'], logo: 'royal-sundaram.png' },
      { id: 'liberty_home', name: 'Liberty General', rating: 3.5, plans: ['Home Protect', 'Comprehensive'], logo: 'liberty-general.png' }
    ],
    travel: [
      { id: 'hdfc_ergo_travel', name: 'HDFC ERGO', rating: 4.1, plans: ['Individual', 'Family', 'Student'], logo: 'hdfc-ergo.png' },
      { id: 'bajaj_allianz_travel', name: 'Bajaj Allianz', rating: 4.0, plans: ['Individual', 'Family', 'Senior'], logo: 'bajaj-allianz.png' },
      { id: 'icici_lombard_travel', name: 'ICICI Lombard', rating: 3.9, plans: ['Individual', 'Family'], logo: 'icici-lombard.png' },
      { id: 'tata_aig_travel', name: 'Tata AIG', rating: 3.8, plans: ['Individual', 'Family'], logo: 'tata-aig.png' },
      { id: 'new_india_travel', name: 'New India Assurance', rating: 3.9, plans: ['Individual', 'Family', 'Student'], logo: 'new-india.png' },
      { id: 'oriental_travel', name: 'Oriental Insurance', rating: 3.7, plans: ['Individual', 'Family'], logo: 'oriental.png' },
      { id: 'united_india_travel', name: 'United India Insurance', rating: 3.6, plans: ['Individual', 'Family'], logo: 'united-india.png' },
      { id: 'national_travel', name: 'National Insurance', rating: 3.5, plans: ['Individual', 'Family'], logo: 'national-insurance.png' },
      { id: 'reliance_travel', name: 'Reliance General', rating: 3.8, plans: ['Individual', 'Family', 'Senior'], logo: 'reliance-general.png' },
      { id: 'future_travel', name: 'Future Generali', rating: 3.7, plans: ['Individual', 'Family'], logo: 'future-generali.png' },
      { id: 'royal_sundaram_travel', name: 'Royal Sundaram', rating: 3.6, plans: ['Individual', 'Family'], logo: 'royal-sundaram.png' },
      { id: 'liberty_travel', name: 'Liberty General', rating: 3.5, plans: ['Individual', 'Family'], logo: 'liberty-general.png' }
    ],
    crop: [
      { id: 'new_india_crop', name: 'New India Assurance', rating: 4.0, plans: ['PMFBY', 'Weather Based', 'Crop Insurance'], logo: 'new-india.png' },
      { id: 'oriental_crop', name: 'Oriental Insurance', rating: 3.8, plans: ['PMFBY', 'Weather Based'], logo: 'oriental.png' },
      { id: 'united_india_crop', name: 'United India Insurance', rating: 3.7, plans: ['PMFBY', 'Crop Insurance'], logo: 'united-india.png' },
      { id: 'national_crop', name: 'National Insurance', rating: 3.6, plans: ['PMFBY', 'Weather Based'], logo: 'national-insurance.png' },
      { id: 'hdfc_ergo_crop', name: 'HDFC ERGO', rating: 3.9, plans: ['PMFBY', 'Weather Based', 'Crop Insurance'], logo: 'hdfc-ergo.png' },
      { id: 'icici_lombard_crop', name: 'ICICI Lombard', rating: 3.8, plans: ['PMFBY', 'Weather Based'], logo: 'icici-lombard.png' }
    ],
    marine: [
      { id: 'new_india_marine', name: 'New India Assurance', rating: 4.1, plans: ['Cargo', 'Hull', 'Protection & Indemnity'], logo: 'new-india.png' },
      { id: 'oriental_marine', name: 'Oriental Insurance', rating: 3.9, plans: ['Cargo', 'Hull'], logo: 'oriental.png' },
      { id: 'united_india_marine', name: 'United India Insurance', rating: 3.8, plans: ['Cargo', 'Hull'], logo: 'united-india.png' },
      { id: 'national_marine', name: 'National Insurance', rating: 3.7, plans: ['Cargo', 'Hull'], logo: 'national-insurance.png' },
      { id: 'hdfc_ergo_marine', name: 'HDFC ERGO', rating: 4.0, plans: ['Cargo', 'Hull', 'Protection & Indemnity'], logo: 'hdfc-ergo.png' },
      { id: 'icici_lombard_marine', name: 'ICICI Lombard', rating: 3.9, plans: ['Cargo', 'Hull'], logo: 'icici-lombard.png' }
    ],
    fire: [
      { id: 'new_india_fire', name: 'New India Assurance', rating: 4.0, plans: ['Standard Fire', 'Industrial All Risks', 'Consequential Loss'], logo: 'new-india.png' },
      { id: 'oriental_fire', name: 'Oriental Insurance', rating: 3.8, plans: ['Standard Fire', 'Industrial All Risks'], logo: 'oriental.png' },
      { id: 'united_india_fire', name: 'United India Insurance', rating: 3.7, plans: ['Standard Fire', 'Industrial All Risks'], logo: 'united-india.png' },
      { id: 'national_fire', name: 'National Insurance', rating: 3.6, plans: ['Standard Fire', 'Industrial All Risks'], logo: 'national-insurance.png' },
      { id: 'hdfc_ergo_fire', name: 'HDFC ERGO', rating: 3.9, plans: ['Standard Fire', 'Industrial All Risks', 'Consequential Loss'], logo: 'hdfc-ergo.png' },
      { id: 'icici_lombard_fire', name: 'ICICI Lombard', rating: 3.8, plans: ['Standard Fire', 'Industrial All Risks'], logo: 'icici-lombard.png' }
    ]
  };

  const coverageAmounts = {
    life: [500000, 1000000, 2500000, 5000000, 10000000],
    health: [300000, 500000, 1000000, 1500000, 2500000],
    motor: [100000, 200000, 500000, 1000000],
    home: [500000, 1000000, 2000000, 5000000],
    travel: [100000, 500000, 1000000, 2000000],
    crop: [10000, 25000, 50000, 100000, 200000],
    marine: [50000, 100000, 250000, 500000, 1000000],
    fire: [100000, 250000, 500000, 1000000, 2500000]
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedPlan('');
    setSelectedCoverage('');
    setQuoteDetails(null);
    setMessage({ type: '', text: '' });
  };

  const validateCustomerDetails = () => {
    const { name, mobile, email, age } = customerDetails;
    
    if (!name.trim()) return 'Please enter your name';
    if (!/^[6-9]\d{9}$/.test(mobile)) return 'Please enter a valid mobile number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email';
    if (!age || age < 18 || age > 80) return 'Age must be between 18 and 80';
    
    return null;
  };

  const calculatePremium = (coverage, age, type) => {
    const baseRates = {
      life: 0.015,    // 1.5% of coverage
      health: 0.035,  // 3.5% of coverage  
      motor: 0.08,    // 8% of coverage
      home: 0.005,    // 0.5% of coverage
      travel: 0.02    // 2% of coverage
    };

    let premium = coverage * baseRates[type];
    
    // Age factor
    if (age > 40) premium *= 1.5;
    if (age > 55) premium *= 2;
    
    // Add random variation
    premium = premium * (0.8 + Math.random() * 0.4);
    
    return Math.round(premium);
  };

  const getQuote = async () => {
    const validationError = validateCustomerDetails();
    if (validationError) {
      setMessage({ type: 'error', text: validationError });
      return;
    }

    if (!selectedPlan) {
      setMessage({ type: 'error', text: 'Please select an insurance provider' });
      return;
    }

    if (!selectedCoverage) {
      setMessage({ type: 'error', text: 'Please select coverage amount' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      const provider = providers[activeTab].find(p => p.id === selectedPlan);
      const premium = calculatePremium(parseInt(selectedCoverage), parseInt(customerDetails.age), activeTab);

      const quote = {
        provider: provider.name,
        plan: provider.plans[0],
        coverage: parseInt(selectedCoverage),
        premium: premium,
        tenure: activeTab === 'travel' ? '1 Trip' : '1 Year',
        features: getInsuranceFeatures(activeTab),
        validTill: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()
      };

      setQuoteDetails(quote);
      setMessage({ 
        type: 'success', 
        text: `Quote generated successfully! Premium: ₹${premium.toLocaleString()}/year` 
      });

    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to generate quote. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const purchasePolicy = async () => {
    if (!quoteDetails) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Simulate policy purchase
      await new Promise(resolve => setTimeout(resolve, 3000));

      const policyData = {
        policyNumber: 'POL' + Date.now(),
        provider: quoteDetails.provider,
        plan: quoteDetails.plan,
        coverage: quoteDetails.coverage,
        premium: quoteDetails.premium,
        customerName: customerDetails.name,
        mobile: customerDetails.mobile,
        status: 'Active',
        issueDate: new Date().toLocaleDateString()
      };

      setMessage({ 
        type: 'success', 
        text: `Policy purchased successfully! Policy Number: ${policyData.policyNumber}` 
      });

      if (onSuccess) {
        onSuccess(policyData);
      }

      // Auto-close after 3 seconds
      setTimeout(() => {
        onHide();
      }, 3000);

    } catch (error) {
      setMessage({ type: 'error', text: 'Policy purchase failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getInsuranceFeatures = (type) => {
    const features = {
      life: ['Death Benefit', 'Tax Benefits', 'Loan Facility', 'Maturity Benefits'],
      health: ['Cashless Treatment', 'Pre/Post Hospitalization', 'Ambulance Cover', 'Day Care Procedures'],
      motor: ['Third Party Cover', 'Own Damage Cover', 'Personal Accident', 'Roadside Assistance'],
      home: ['Structure Cover', 'Contents Cover', 'Temporary Accommodation', 'Personal Liability'],
      travel: ['Medical Emergency', 'Trip Cancellation', 'Baggage Loss', 'Flight Delay']
    };
    return features[type] || [];
  };

  const handleReset = () => {
    setSelectedPlan('');
    setSelectedCoverage('');
    setQuoteDetails(null);
    setMessage({ type: '', text: '' });
    setCustomerDetails({
      name: '',
      mobile: '',
      email: '',
      age: '',
      gender: ''
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaShieldAlt className="me-2" />
          Insurance Services
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Insurance Type Tabs */}
        <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-4">
          {insuranceTypes.map((type) => (
            <Tab 
              key={type.key}
              eventKey={type.key} 
              title={
                <span>
                  {type.icon}
                  <span className="ms-2">{type.label}</span>
                </span>
              }
            >
              <div className="p-2">
                <h6 className="text-primary mb-3">{type.label}</h6>
              </div>
            </Tab>
          ))}
        </Tabs>

        <Row>
          {/* Left Column - Customer Details */}
          <Col md={4}>
            <Card className="mb-4">
              <Card.Header>
                <FaUser className="me-2" />
                Customer Details
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter full name"
                    value={customerDetails.name}
                    onChange={(e) => setCustomerDetails({...customerDetails, name: e.target.value})}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter mobile number"
                    value={customerDetails.mobile}
                    onChange={(e) => setCustomerDetails({...customerDetails, mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    value={customerDetails.email}
                    onChange={(e) => setCustomerDetails({...customerDetails, email: e.target.value})}
                  />
                </Form.Group>

                <Row>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>Age *</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Age"
                        value={customerDetails.age}
                        onChange={(e) => setCustomerDetails({...customerDetails, age: e.target.value})}
                        min="18"
                        max="80"
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>Gender</Form.Label>
                      <Form.Select
                        value={customerDetails.gender}
                        onChange={(e) => setCustomerDetails({...customerDetails, gender: e.target.value})}
                      >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column - Plan Selection */}
          <Col md={8}>
            {/* Provider Selection */}
            <div className="mb-4">
              <h6 className="mb-3">Select Insurance Provider</h6>
              <Row>
                {providers[activeTab]?.map((provider) => (
                  <Col md={6} lg={4} key={provider.id} className="mb-3">
                    <Card 
                      className={`h-100 cursor-pointer ${selectedPlan === provider.id ? 'border-primary' : ''}`}
                      onClick={() => setSelectedPlan(provider.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Body className="text-center p-3">
                        <FaShieldAlt className="text-primary mb-2" size={24} />
                        <h6 className="mb-1">{provider.name}</h6>
                        <div className="mb-2">
                          <Badge bg="warning">⭐ {provider.rating}</Badge>
                        </div>
                        <small className="text-muted">
                          {provider.plans.slice(0, 2).join(', ')}
                        </small>
                        {selectedPlan === provider.id && (
                          <div className="mt-2">
                            <FaCheckCircle className="text-success" />
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>

            {/* Coverage Amount */}
            {selectedPlan && (
              <div className="mb-4">
                <h6 className="mb-3">Select Coverage Amount</h6>
                <div className="d-flex flex-wrap gap-2">
                  {coverageAmounts[activeTab]?.map((amount) => (
                    <Button
                      key={amount}
                      variant={selectedCoverage === amount.toString() ? "primary" : "outline-primary"}
                      size="sm"
                      onClick={() => setSelectedCoverage(amount.toString())}
                    >
                      ₹{(amount / 100000).toFixed(0)}L
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Quote Details */}
            {quoteDetails && (
              <Card className="mb-4">
                <Card.Header className="bg-success text-white">
                  <FaCheckCircle className="me-2" />
                  Insurance Quote
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-2"><strong>Provider:</strong> {quoteDetails.provider}</div>
                      <div className="mb-2"><strong>Plan:</strong> {quoteDetails.plan}</div>
                      <div className="mb-2"><strong>Coverage:</strong> ₹{quoteDetails.coverage.toLocaleString()}</div>
                      <div className="mb-2"><strong>Tenure:</strong> {quoteDetails.tenure}</div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2"><strong>Premium:</strong> ₹{quoteDetails.premium.toLocaleString()}</div>
                      <div className="mb-2"><strong>Valid Till:</strong> {quoteDetails.validTill}</div>
                      <div className="mb-2">
                        <strong>Features:</strong>
                        <ul className="mb-0 mt-1">
                          {quoteDetails.features.slice(0, 3).map((feature, index) => (
                            <li key={index} className="small">{feature}</li>
                          ))}
                        </ul>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        {/* Message */}
        {message.text && (
          <Alert variant={message.type === 'error' ? 'danger' : 'success'} className="mb-3">
            {message.text}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {selectedPlan && (
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Cancel
            </Button>
            {!quoteDetails ? (
              <Button 
                variant="primary" 
                onClick={getQuote}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Getting Quote...
                  </>
                ) : (
                  <>
                    <FaRupeeSign className="me-2" />
                    Get Quote
                  </>
                )}
              </Button>
            ) : (
              <Button 
                variant="success" 
                onClick={purchasePolicy}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FaShieldAlt className="me-2" />
                    Buy Policy ₹{quoteDetails.premium.toLocaleString()}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default InsuranceModal;
